/* eslint-disable @typescript-eslint/no-explicit-any */
import { ref, computed, type Ref, type ComputedRef } from "vue";
import { useRoute, useRouter } from "vue-router";
import dayjs from "dayjs";
import type { SearchFilters } from "@/types/domain";
import {
  allowsInPerson,
  allowsOnline,
  type AttendanceMode,
} from "@/constants/attendanceMode";
import { availabilitySummary } from "@/services/naaviApi/internalService";
import { searchClasses } from "@/services/naaviApi/classesService";
import { normalizeClassPrice } from "@/helpers/classPriceHelper";
import { toStartOfDayIso, extractDatePart } from "@/helpers/dateHelper";
import { mapAvailabilitySummariesToDoctors } from "@/helpers/availabilitySummaryMapper";
import {
  buildSearchFiltersFromQuery,
  resolveSearchRadiusKm,
} from "@/helpers/searchFilterHelper";
import {
  buildClassMapKey,
  mergeClassesIntoDoctors,
  reuseExistingDoctorState,
  isSameDoctorEntry,
} from "@/helpers/searchDoctorHelper";
import { useGeolocation } from "./useGeolocation";
import { useCalendarNavigation } from "./useCalendarNavigation";
import { usePagination } from "./usePagination";

const SEARCH_TYPE_FULL_DAY = 2;
const DEFAULT_CALENDAR_DAYS = 4;
const DEFAULT_LOCATION_RADIUS_KM = 10;
const DEFAULT_PAGE_SIZE = 20;
const SEARCH_RADIUS_OPTIONS = [1, 2, 3, 10, 25, 50];

export interface DoctorSearchOptions {
  translateFn?: (key: string) => string;
}

export function useSearch(options: DoctorSearchOptions = {}) {
  const route = useRoute();
  const router = useRouter();
  const { translateFn } = options;

  // State
  const doctors = ref<any[]>([]);
  const filters = ref<SearchFilters>(getFiltersFromUrl());
  const availabilityLoading = ref(false);
  const currentWindowStart = ref<Date | null>(null);
  const searchCycleId = ref(0);
  const hasSearched = ref(false);
  const classesByTherapist = ref<Map<string, any[]>>(new Map());
  const classesLoaded = ref(false);
  const lastSearchKey = ref<string | null>(null);

  // Composables
  const geolocation = useGeolocation();

  const calendarNavigation = useCalendarNavigation({
    doctors,
    currentWindowStart,
    filters: computed(() => ({
      online: filters.value.online,
      inPerson: filters.value.inPerson,
      therapy: filters.value.therapy,
      therapyType: filters.value.therapyType,
      location: filters.value.location,
      radiusKm: filters.value.radiusKm,
    })),
  });

  const pagination = usePagination(doctors, loadMore, {
    pageSize: DEFAULT_PAGE_SIZE,
  });

  // Computed
  const datesLoading = computed(
    () =>
      availabilityLoading.value ||
      calendarNavigation.calendarLoadingCount.value > 0,
  );

  // Methods
  function getFiltersFromUrl(): SearchFilters {
    const query = route?.query ?? {};
    return buildSearchFiltersFromQuery(
      query,
      SEARCH_RADIUS_OPTIONS,
      process.env.URL_PARAM_DATE_FORMAT,
    );
  }

  async function initializeLocation(): Promise<void> {
    const query = route?.query ?? {};
    const latParam =
      typeof query.lat === "string" ? Number.parseFloat(query.lat) : NaN;
    const lngParam =
      typeof query.lng === "string" ? Number.parseFloat(query.lng) : NaN;
    const hasQueryCoordinates =
      Number.isFinite(latParam) && Number.isFinite(lngParam);
    const hasQueryAddress = typeof query.address === "string";

    if (hasQueryCoordinates || hasQueryAddress) return;

    // Try loading from storage first
    if (geolocation.loadFromStorage()) {
      filters.value.location = geolocation.location.value;
      filters.value.radiusKm = geolocation.radiusKm.value;
      return;
    }

    // Request geolocation if not in storage
    const success = await geolocation.requestGeolocation(navigator.language);
    if (success) {
      filters.value.location = geolocation.location.value;
      filters.value.radiusKm = geolocation.radiusKm.value;
    }
  }

  function resolveBaseDate(overrideDate?: Date | string | null): Date {
    const value = overrideDate ?? filters.value.dateFrom ?? new Date();

    if (value instanceof Date) {
      const copy = new Date(value.getTime());
      return Number.isNaN(copy.getTime()) ? new Date() : copy;
    }

    const rawValue = String(value);
    const datePart = extractDatePart(rawValue);
    if (datePart) {
      const parsed = new Date(`${datePart}T00:00:00`);
      if (!Number.isNaN(parsed.getTime())) return parsed;
    }

    const parsed = new Date(rawValue);
    return Number.isNaN(parsed.getTime()) ? new Date() : parsed;
  }

  async function loadClasses(baseDate: Date): Promise<void> {
    if (classesLoaded.value) return;

    const latitude = filters.value.location.lat;
    const longitude = filters.value.location.lng;
    const radiusKm = filters.value.radiusKm;

    const classPayload: Record<string, unknown> = {
      date: toStartOfDayIso(baseDate),
      includeOnline: filters.value.online,
      includeInPerson: filters.value.inPerson,
      latitude: !filters.value.online ? latitude : undefined,
      longitude: !filters.value.online ? longitude : undefined,
      therapy: filters.value.therapy || undefined,
      therapyType: filters.value.therapyType || undefined,
    };

    if (typeof radiusKm === "number" && Number.isFinite(radiusKm)) {
      classPayload.radiusKm = radiusKm;
    }

    try {
      const classesResponse = await searchClasses(classPayload);
      let rawClasses = Array.isArray(classesResponse?.items)
        ? classesResponse.items
        : [];

      // Fallback search without therapy filters
      if (
        !rawClasses.length &&
        (filters.value.therapy || filters.value.therapyType)
      ) {
        const fallbackPayload = {
          ...classPayload,
          therapy: undefined,
          therapyType: undefined,
        };
        const fallbackResponse = await searchClasses(fallbackPayload);
        rawClasses = Array.isArray(fallbackResponse?.items)
          ? fallbackResponse.items
          : [];
      }

      const classItems = rawClasses.map((item: any) =>
        normalizeClassPrice(item),
      );

      classesByTherapist.value = classItems.reduce(
        (map: Map<string, any[]>, current: any) => {
          if (!current) return map;

          const tenantKey = current.tenantXId;
          const therapistId = current.therapist?.id ?? null;
          const classKey = buildClassMapKey(tenantKey, therapistId);

          if (!classKey) return map;

          const existing = map.get(classKey);
          if (existing) {
            existing.push(current);
          } else {
            map.set(classKey, [current]);
          }
          return map;
        },
        new Map<string, any[]>(),
      );

      classesLoaded.value = true;
    } catch {
      // Silently fail for classes
    }
  }

  interface SearchOptions {
    overrideDate?: Date | string | null;
    skipFilterRefresh?: boolean;
    returnResultOnly?: boolean;
    skipClasses?: boolean;
    append?: boolean;
    page?: number;
  }

  async function search(searchOptions: SearchOptions = {}): Promise<any[]> {
    const {
      overrideDate = null,
      skipFilterRefresh = false,
      returnResultOnly = false,
      skipClasses = false,
      append = false,
      page = 1,
    } = searchOptions;

    if (!skipFilterRefresh) {
      filters.value = getFiltersFromUrl();
    }

    const previousDoctors = [...doctors.value];
    const isAppend = append === true;

    const baseDate = resolveBaseDate(overrideDate);
    baseDate.setHours(0, 0, 0, 0);

    if (!returnResultOnly && !isAppend) {
      filters.value.dateFrom = baseDate;
      currentWindowStart.value = baseDate;
    }

    if (!isAppend) {
      pagination.reset();
      calendarNavigation.calendarRequestsInFlight.value = {};
      classesByTherapist.value = new Map();
      classesLoaded.value = false;
    }

    const latitude = filters.value.location.lat;
    const longitude = filters.value.location.lng;
    const hasLocation =
      typeof latitude === "number" && typeof longitude === "number";
    const radiusKm =
      !filters.value.online && hasLocation
        ? resolveSearchRadiusKm(
            filters.value.radiusKm ?? DEFAULT_LOCATION_RADIUS_KM,
            SEARCH_RADIUS_OPTIONS,
          ) ?? DEFAULT_LOCATION_RADIUS_KM
        : null;

    filters.value.radiusKm = radiusKm;

    const resolvedPage =
      Number.isFinite(Number(page)) && Number(page) > 0 ? Number(page) : 1;

    const searchParams = {
      date: toStartOfDayIso(baseDate),
      concept: 0,
      searchType: SEARCH_TYPE_FULL_DAY,
      therapy: filters.value.therapy || undefined,
      therapyType: filters.value.therapyType || undefined,
      latitude: latitude,
      longitude: longitude,
      includeOnline: filters.value.online,
      includeInPerson: filters.value.inPerson,
      page: resolvedPage,
      pageSize: DEFAULT_PAGE_SIZE,
      radius: typeof radiusKm === "number" ? radiusKm : undefined,
    };

    if (!returnResultOnly && !isAppend) {
      availabilityLoading.value = true;
    }

    // Load classes if needed
    if (!skipClasses && !isAppend && !classesLoaded.value) {
      await loadClasses(baseDate);
    }

    try {
      const searchKey = buildSearchKey(baseDate, latitude, longitude, radiusKm);
      const reuseSchedule =
        isAppend ||
        (lastSearchKey.value !== null && lastSearchKey.value === searchKey);

      if (!isAppend) {
        lastSearchKey.value = searchKey;
      }

      const response = await availabilitySummary(searchParams);
      const availabilityItems = Array.isArray(response?.items)
        ? response.items
        : [];

      // Filter by online/in-person mode
      const filteredItems = filterByMode(availabilityItems);

      const newDoctors = mapAvailabilitySummariesToDoctors(
        filteredItems as any[],
        {
          baseDateIso: toStartOfDayIso(baseDate),
          translate: translateFn,
        },
      );

      // Merge classes
      if (classesByTherapist.value.size) {
        mergeClassesIntoDoctors(newDoctors as any[], classesByTherapist.value);
      }

      // Reuse existing state
      reuseExistingDoctorState(newDoctors as any[], previousDoctors as any[], {
        reuseSchedule,
      });

      // Update cycle ID
      const nextCycleId = (searchCycleId.value || 0) + 1;
      searchCycleId.value = nextCycleId;
      newDoctors.forEach((doctor: any) => {
        doctor.searchCycleId = nextCycleId;
      });

      // Merge or replace doctors
      let mergedDoctors = newDoctors;
      if (isAppend) {
        const combined = [...previousDoctors];
        newDoctors.forEach((doctor: any) => {
          const exists = combined.some((entry) =>
            isSameDoctorEntry(entry, doctor),
          );
          if (!exists) combined.push(doctor);
        });
        mergedDoctors = combined;
      }

      doctors.value = mergedDoctors;

      // Update pagination
      const responseMeta = response?.meta;
      pagination.updateHasMore(newDoctors.length, responseMeta);

      if (returnResultOnly) {
        return doctors.value;
      }
    } catch {
      if (!isAppend) {
        doctors.value = [];
      }
      if (returnResultOnly) {
        return [];
      }
    } finally {
      if (!isAppend) {
        availabilityLoading.value = false;
      }
      if (!returnResultOnly && !isAppend) {
        hasSearched.value = true;
      }
    }

    return doctors.value;
  }

  async function loadMore(): Promise<void> {
    if (
      pagination.isLoadingMore.value ||
      availabilityLoading.value ||
      !pagination.hasMoreResults.value
    ) {
      return;
    }

    await search({
      page: pagination.currentPage.value + 1,
      append: true,
      skipFilterRefresh: true,
      skipClasses: true,
    });
  }

  function buildSearchKey(
    baseDate: Date,
    latitude: number | null,
    longitude: number | null,
    radiusKm: number | null,
  ): string {
    return [
      toStartOfDayIso(baseDate),
      filters.value.online ? "1" : "0",
      filters.value.therapy || "",
      filters.value.therapyType || "",
      typeof latitude === "number" ? String(latitude) : "",
      typeof longitude === "number" ? String(longitude) : "",
      typeof radiusKm === "number" ? String(radiusKm) : "",
    ].join("|");
  }

  function filterByMode(items: unknown[]): unknown[] {
    const resolveModes = (item: Record<string, unknown>) => {
      const optionsList = Array.isArray(item?.bookingOptions)
        ? item.bookingOptions
        : [];
      let hasOnline = false;
      let hasInPerson = false;

      optionsList.forEach((option: Record<string, unknown>) => {
        if (!option) return;
        const mode = option.attendanceMode as AttendanceMode | undefined;
        if (allowsOnline(mode)) hasOnline = true;
        if (allowsInPerson(mode)) hasInPerson = true;
      });

      return { hasOnline, hasInPerson };
    };

    const onlyOnline = filters.value.online && !filters.value.inPerson;
    const onlyInPerson = filters.value.inPerson && !filters.value.online;

    if (onlyOnline) {
      return items.filter(
        (item) => resolveModes(item as Record<string, unknown>).hasOnline,
      );
    }
    if (onlyInPerson) {
      return items.filter(
        (item) => resolveModes(item as Record<string, unknown>).hasInPerson,
      );
    }
    return items;
  }

  async function handleSearchArea(payload: {
    latitude: number | null;
    longitude: number | null;
    radiusKm?: number | null;
  }): Promise<void> {
    const { latitude, longitude, radiusKm: payloadRadius } = payload;

    if (latitude === null || longitude === null || filters.value.online) {
      return;
    }

    const nextRadius =
      resolveSearchRadiusKm(payloadRadius, SEARCH_RADIUS_OPTIONS) ??
      resolveSearchRadiusKm(
        filters.value.radiusKm ?? DEFAULT_LOCATION_RADIUS_KM,
        SEARCH_RADIUS_OPTIONS,
      ) ??
      DEFAULT_LOCATION_RADIUS_KM;

    const nextDate = filters.value.dateFrom ?? new Date();

    filters.value = {
      ...filters.value,
      online: false,
      inPerson: true,
      location: {
        address: "",
        lat: latitude,
        lng: longitude,
        placeId: null,
      },
      radiusKm: nextRadius,
    };

    const dateParam = dayjs(nextDate).format(process.env.URL_PARAM_DATE_FORMAT);

    await router.replace({
      path: "/search",
      query: {
        lat: String(latitude),
        lng: String(longitude),
        address: "",
        online: "0",
        radius: Number.isFinite(nextRadius) ? String(nextRadius) : undefined,
        dateFrom: dateParam,
        therapy: filters.value.therapy || undefined,
        therapyType: filters.value.therapyType || undefined,
      },
    });

    await search({
      overrideDate: nextDate,
      skipFilterRefresh: true,
    });
  }

  return {
    // State
    doctors,
    filters,
    availabilityLoading,
    currentWindowStart,
    hasSearched,
    datesLoading,

    // Pagination
    hasMoreResults: pagination.hasMoreResults,
    isLoadingMore: pagination.isLoadingMore,
    sentinelRef: pagination.sentinelRef,

    // Calendar navigation
    calendarLoadingCount: calendarNavigation.calendarLoadingCount,
    handleRequestMoreDates: calendarNavigation.handleRequestMoreDates,
    handleRequestCalendar: calendarNavigation.handleRequestCalendar,

    // Methods
    initializeLocation,
    search,
    loadMore,
    handleSearchArea,
  };
}
